import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiMetric,
} from "@incendium/api";
import { Box } from "@mui/material";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { SalesFlowFunnelChart } from "features/salesFlow";
import { useIsMobile } from "Hooks/useIsMobile";
import { useIsEcommerceProject } from "features/dashboard/utils/ecommerceMetrics";

interface IFunnelTabProps {
  attributes: ApiChartAttribute[];
}

function FunnelTab({ attributes }: IFunnelTabProps) {
  const isMobile = useIsMobile();
  const isEcommerce = useIsEcommerceProject();

  return (
    <>
      {isEcommerce && (
        <Box
          sx={{
            height: { xs: 360, md: 420 },
            flexShrink: 0,
            width: "100%",
            minWidth: 0,
            overflow: "auto",
          }}
        >
          <AnalyticsCard
            ignoreAttrModel
            noToolbar
            chart={{
              name: "Ecommerce Conversion Funnel",
              dimension: [],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.FUNNEL,
              comparison: true,
              attributes,
              displayOptions: {
                funnelUseLogFn: true,
                noAttributionToggle: true,
                funnelAlwaysShowAdditionalMetrics: isMobile,
                metricNames: [
                  {
                    metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                    name: "Sessions with Add to Cart",
                  },
                  {
                    metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                    name: "Total Add to Carts",
                  },
                  {
                    metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                    name: "Add to Cart Rate",
                  },
                  {
                    metric: ApiMetric.METRIC_SALES_COUNT_LAST,
                    name: "Total Sales",
                  },
                  {
                    metric: ApiMetric.METRIC_REVENUE_LAST,
                    name: "Total Revenue",
                  },
                  {
                    metric: ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                    name: "Total New Customers",
                  },
                  {
                    metric: ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                    name: "AOV",
                  },
                  {
                    metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                    name: "Total Product Pageviews",
                  },
                  {
                    metric: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                    name: "Sessions where PDP viewed",
                  },
                  {
                    metric: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                    name: "PDP viewed rate",
                  },
                  {
                    metric: ApiMetric.METRIC_CHECKOUT_START_COUNT,
                    name: "Sessions with Checkout Start",
                  },
                ],
                funnelAdditonalMetrics: [
                  {
                    key: ApiMetric.METRIC_SESSIONS_COUNT,
                    metrics: [
                      ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                      ApiMetric.METRIC_PAID_SESSION_COUNT,
                      ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
                    ],
                  },
                  {
                    key: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                    metrics: [
                      ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                      ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                      ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
                      ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    ],
                  },
                  {
                    key: ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                    metrics: [
                      ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                      ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                      ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                    ],
                  },
                  {
                    key: ApiMetric.METRIC_CHECKOUT_START_COUNT,
                    metrics: [
                      ApiMetric.METRIC_CHECKOUT_START_RATE,
                      ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
                    ],
                  },
                  {
                    key: ApiMetric.METRIC_SALES_COUNT_LAST,
                    metrics: [
                      ApiMetric.METRIC_REVENUE_LAST,
                      ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                      ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                    ],
                  },
                ],
              },
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_SESSIONS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                    ApiMetric.METRIC_PAID_SESSION_COUNT,
                    ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
                    ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                    ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                    ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                    ApiMetric.METRIC_CHECKOUT_START_COUNT,
                    ApiMetric.METRIC_CHECKOUT_START_RATE,
                    ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
                    ApiMetric.METRIC_SALES_COUNT_LAST,
                    ApiMetric.METRIC_REVENUE_LAST,
                    ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                    ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                  ],
                },
              ],
            }}
          />
        </Box>
      )}

      <SalesFlowFunnelChart attributes={attributes} ignoreAttrModel />
    </>
  );
}

export default FunnelTab;
